import { memo, useEffect, useRef, useState } from "react";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";
import ChevronRight from "@zenith/assets/icons/ChevronRight";
import { PaymentSalesCardComponent } from "@zenith/features/Analytics";
import { PaymentChargedDTO } from "@zenith/services/transactions";

export interface PaymentSalesCarouselComponentProps {
  itemsList: PaymentChargedDTO[];
  isWeekly: boolean;
}

const PaymentSalesCarouselComponent = memo(
  function PaymentSalesCarouselComponent(
    props: PaymentSalesCarouselComponentProps
  ) {
    const { itemsList, isWeekly } = props;

    const carouselRef = useRef<HTMLDivElement | null>(null);
    const [showOverflow, setShowOverflow] = useState<boolean>(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeftStart, setScrollLeftStart] = useState(0);

    const checkScrollPosition = () => {
      const carousel = carouselRef.current;
      if (!carousel) return;

      setCanScrollLeft(carousel.scrollLeft > 0);
      setCanScrollRight(
        carousel.scrollLeft + carousel.clientWidth < carousel.scrollWidth - 1
      );
    };

    const scroll = (direction: "left" | "right") => {
      const carousel = carouselRef.current;
      if (!carousel) return;

      const scrollAmount = carousel.clientWidth * 0.8;

      if (direction === "left") {
        carousel.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      } else {
        carousel.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      const carousel = carouselRef.current;
      if (!carousel) return;

      setIsDragging(true);
      setStartX(e.pageX - carousel.offsetLeft);
      setScrollLeftStart(carousel.scrollLeft);
      carousel.style.cursor = "grabbing";
      carousel.style.userSelect = "none";
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging) return;
      const carousel = carouselRef.current;
      if (!carousel) return;

      e.preventDefault();
      const x = e.pageX - carousel.offsetLeft;
      const walk = (x - startX) * 1.5;
      carousel.scrollLeft = scrollLeftStart - walk;
    };

    const handleMouseUp = () => {
      const carousel = carouselRef.current;
      if (!carousel) return;

      setIsDragging(false);
      carousel.style.cursor = "grab";
      carousel.style.userSelect = "auto";
      checkScrollPosition();
    };

    const handleMouseLeave = () => {
      if (isDragging) {
        const carousel = carouselRef.current;
        if (carousel) {
          carousel.style.cursor = "grab";
          carousel.style.userSelect = "auto";
        }
        setIsDragging(false);
        checkScrollPosition();
      }
    };

    useEffect(() => {
      const carousel = carouselRef.current;
      if (!carousel) return;

      const checkOverflow = () => {
        setShowOverflow(carousel.scrollWidth > carousel.clientWidth);
        checkScrollPosition();
      };

      const timeoutId = setTimeout(checkOverflow, 500);

      carousel.addEventListener("scroll", checkScrollPosition);
      window.addEventListener("resize", checkOverflow);

      const observer = new MutationObserver(checkOverflow);
      observer.observe(carousel, { childList: true, subtree: true });

      carousel.style.cursor = "grab";

      return () => {
        clearTimeout(timeoutId);
        carousel.removeEventListener("scroll", checkScrollPosition);
        window.removeEventListener("resize", checkOverflow);
        observer.disconnect();
      };
    }, [itemsList]);

    return (
      <>
        <div className="inline-flex h-7 w-full items-center justify-between pl-4">
          <div className="text-xl font-medium leading-7 text-zinc-900">
            Sales by Payment Method
          </div>
          {showOverflow && (
            <div className="flex">
              <button
                className="text-neutral-600 disabled:text-neutral-200"
                disabled={!canScrollLeft}
                onClick={() => scroll("left")}
              >
                <ChevronLeft color="currentColor" />
              </button>
              <button
                className="text-neutral-600 disabled:text-neutral-200"
                disabled={!canScrollRight}
                onClick={() => scroll("right")}
              >
                <ChevronRight color="currentColor" />
              </button>
            </div>
          )}
        </div>
        <div
          ref={carouselRef}
          className="flex w-auto flex-row gap-4 overflow-x-hidden p-1"
          id="analytics-carousel"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          style={{
            scrollBehavior: "auto",
          }}
        >
          {itemsList?.map((item, index) => (
            <PaymentSalesCardComponent
              key={index}
              rate={item.rate as number}
              isWeekly={isWeekly}
              averageSalesValue={item.avg as number}
              numberOfTransactions={item.quantity as number}
              paymentMethod={item.paymentMethod?.mapPaymentMethod() as string}
              paymentMethodSalesValue={item.amount as number}
            />
          ))}
        </div>
      </>
    );
  }
);

export default PaymentSalesCarouselComponent;